import { Box, Button, CircularProgress, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import AddIcon from '@mui/icons-material/Add';
import { useContext, useState } from "react";
import CustomInput from "../../inputs/customInput";
import { useTheme } from "@emotion/react";
import DateInput from "../../inputs/dateInput";
import dayjs from "dayjs";
import WalletTransferService from "../../../services/walletTransferService";
import TransferTypeDropdown from "../../dropdowns/transferTypeDropdown";
import { AuthContext } from "../../../provider/authProvider";
import BranchDropdown from "../../dropdowns/branchDropdown";

const AddTransferBox = ({
    handleReload,
    setPopupMsg,
    setPopupType,
    setShowPopup }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { user } = useContext(AuthContext);
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const userPermissions = user?.permissions || [];
    const allBranchPermission = userPermissions?.some(p => p.value === 'allBranches');

    const [formValues, setFormValues] = useState({
        amount: 0,
        type: null,
        branchId: null,
        note: '',
    });

    const [addErrors, setAddErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [selectedType, setSelectedType] = useState(null);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [selectedBranch, setSelectedBranch] = useState(null);

    const handleDateChange = (date) => {
        setSelectedDate(date);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    const handleNumberChange = (e) => {
        const { name, value } = e.target;
        const numericValue = value.replace(/,/g, "");

        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: numericValue,
        }));
    };

    const validateForm = () => {
        const newAddErrors = {};

        if (!formValues.amount) {
            newAddErrors.amount = t('amountRequired');
        }
        if (isNaN(formValues.amount)) {
            newAddErrors.amount = t('amountNumber');
        }

        if (!formValues.type) {
            newAddErrors.type = t('typeRequired');
        }

        setAddErrors(newAddErrors);

        return Object.keys(newAddErrors).length === 0;
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLoading) return;

        if (validateForm()) {
            try {
                setIsLoading(true);
                console.log();

                const data = await WalletTransferService.add({ ...formValues, date: selectedDate });
                if (data.status === 200) {
                    setPopupMsg(data.data.message);
                    setPopupType('success');
                    setShowPopup(true);
                    handleReload();
                    setFormValues({ amount: 0, note: '' });
                    setSelectedType(null);
                    setSelectedBranch(null);
                    setSelectedDate(dayjs());
                }

                setAddErrors({});
            } catch (error) {
                if (error.response?.status === 400) {
                    const responseErrors = error.response.data || {};
                    const formattedErrors = {};

                    Object.keys(responseErrors).forEach((field) => {
                        formattedErrors[field] = responseErrors[field];
                    });

                    setAddErrors(formattedErrors);
                } else {
                    setPopupMsg(error.response.data.error);
                    setPopupType('error');
                    console.error("An unexpected error occurred:", error);
                    setAddErrors({ general: error.response.data.error });
                }
            } finally {
                setIsLoading(false);
            }
        }
    };



    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                width: '100%',
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: 1,
                borderColor: theme.palette.customGrey.borderInput,
                px: isMobile ? 1 : 4,
                py: 1
            }}
        >
            {/* <Box sx={{
                display: 'flex',
                justifyContent: 'space-between'
            }}>
                <Typography id="transition-modal-title" variant="h6" component="h2">
                    {t('addWalletTransfer')}
                </Typography>
            </Box> */}

            {
                allBranchPermission &&
                <BranchDropdown
                    helperText={addErrors.branch}
                    error={!!addErrors.branch}
                    setFormValues={setFormValues}
                    isFilter={false}
                    selectedBranch={selectedBranch}
                    setSelectedBranch={setSelectedBranch}
                />
            }

            <TransferTypeDropdown
                helperText={addErrors.type}
                error={!!addErrors.type}
                setFormValues={setFormValues}
                isFilter={false}
                selectedType={selectedType}
                setSelectedType={setSelectedType}
            />

            <CustomInput
                label={t('amount')}
                placeholder={t('amountHere')}
                name="amount"
                type='text'
                value={formValues.amount ? Number(formValues.amount).toLocaleString() : 0}
                error={!!addErrors.amount}
                helperText={addErrors.amount}
                onChange={handleNumberChange}
            />

            <DateInput
                label={t('date')}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                handleDateChange={handleDateChange}
            />

            <CustomInput
                label={t('note')}
                placeholder={t('noteHere')}
                name="note"
                type='text'
                value={formValues.note}
                onChange={handleChange}
            />

            {addErrors.general && (
                <Typography align="center" color="error" variant="body2" sx={{ mt: 2 }}>
                    {addErrors.general}
                </Typography>
            )}

            <Box sx={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                mt: addErrors.general ? 1 : 2
            }}>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ textTransform: 'none', width: 140, height: 40 }}
                >
                    {isLoading ? <CircularProgress color="secondary" size={28} /> :
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <p>{t('add')}</p>
                            <AddIcon fontSize="small" sx={{ mx: 0.4 }} />
                        </Box>
                    }
                </Button>
            </Box>
        </Box>
    );
}

export default AddTransferBox;
